import { cloneDeep } from 'lodash';
import { z } from 'zod';

export const IntId = z.object({
    id: z.number().int(),
});

export type IntId = z.infer<typeof IntId>;

export const IdObject = z.object({
    id: z.number().or(z.string()),
});

export type IdObject = z.infer<typeof IdObject>;

export const mergeObjectId = <T extends IdObject, Return = T | T[] | { data: T[] } | undefined>(
    source: T | T[] | undefined,
    target: T | T[] | { data: T[] } | undefined,
    remove = false,
    opts: { deep?: (keyof T)[]; transformOthers?: (value: T) => T; removeDeep?: boolean } = {},
): Return => {
    target = cloneDeep(target);
    source = cloneDeep(source);

    if (target == null) {
        return target as Return;
    }

    if (source == null) {
        return target as Return;
    }

    if (Array.isArray(source)) {
        for (const s of source) {
            target = mergeObjectId(s, target, remove, opts);
        }

        return target as Return;
    }

    if (Array.isArray(target)) {
        if (remove) {
            target = target.filter(t => t.id !== source.id);
        } else {
            const idx = target.findIndex(t => t.id === source.id);
            if (idx === -1) {
                target.unshift(source);
            } else {
                target[idx] = { ...target[idx], ...source };
                if (opts.transformOthers) {
                    for (let i = 0; i < target.length; i++) {
                        if (i !== idx) {
                            target[i] = opts.transformOthers(target[i] as T);
                        }
                    }
                }
            }
        }
    } else {
        if ('data' in target) {
            target.data = mergeObjectId(source, target.data, remove, opts);
            return target as Return;
        }

        if (remove) {
            target = undefined;
        } else if (target.id === source.id) {
            if (opts.deep) {
                for (const key of opts.deep) {
                    if (target[key] && source[key]) {
                        source[key] = mergeObjectId(source[key] as any, target[key] as any, opts?.removeDeep ?? false);
                    }
                }
            }
            target = { ...target, ...source };
        }
    }

    return target as Return;
};
